import { useState, useEffect } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuthReady, setIsAuthReady] = useState(false)

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
        setIsAuthReady(true)
      }
    )

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
      setIsAuthReady(true)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error }
  }

  const signUp = async (email: string, password: string, fullName?: string, company?: string, role?: string) => {
    const redirectUrl = `${window.location.origin}/auth/callback`
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          company: company,
          role: role || 'buyer'
        }
      }
    })

    // Check if this is a repeated signup (user already exists)
    if (data.user && data.user.identities && data.user.identities.length === 0) {
      return { 
        data, 
        error: { 
          message: 'User with this email already registered. Please sign in instead.',
          name: 'AuthApiError'
        } as any
      }
    }

    return { data, error }
  }

  const signUpWithPhone = async (phone: string, password: string, fullName?: string, company?: string, role?: string) => {
    const { data, error } = await supabase.auth.signUp({
      phone,
      password,
      options: {
        data: {
          full_name: fullName,
          company: company,
          role: role || 'buyer'
        }
      }
    })

    return { data, error }
  }

  const verifyOtp = async (phone: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms'
    })
    return { data, error }
  }

  const signInWithPhone = async (phone: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      phone,
      password,
    })
    return { error }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  const getUserProfile = async (userId: string) => {
    // Get user roles from user_roles table
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
    
    if (error) {
      console.error('Error fetching user roles:', error)
      return null
    }
    
    return data
  }

  return {
    user,
    session,
    loading,
    isAuthReady,
    signIn,
    signInWithPhone,
    signUp,
    signUpWithPhone,
    verifyOtp,
    signOut,
    getUserProfile,
  }
}